import React, { useContext, useEffect, useState } from 'react';

import { NonCommerceInfoContext, ProductContainerContext } from '@rsc/contexts';

import { cn } from '@rsc/tailwind-base/utils';

import { Button } from '@rsc/ui-components/shadcn/Button';
import LargeCartButton from '@rsc/ui-components/tailwind/LargeCartButton';
import QuantityCounter from '@rsc/ui-components/tailwind/QuantityCounter';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';

const isObsoleteOrComingSoon = lifecycle =>
  lifecycle === 'Obsolete' || lifecycle === 'Coming Soon';

interface Props {
  buttonFull?: boolean;
}

const AddToCartWithQuantity = ({ buttonFull = true }: Readonly<Props>) => {
  const { isNonCommerce } = useContext(NonCommerceInfoContext) as any;
  const { product, activeVariant, quantity, setQuantity, productLifecycle } =
    useContext(ProductContainerContext) as any;
  const [isAvailableForSale, setIsAvailableForSale] = useState(true);
  const [quantityAvailable, setQuantityAvailable] = useState(0);
  const { tt } = useUiComponentsInternationalContext();

  useEffect(() => {
    if (activeVariant) {
      setIsAvailableForSale(activeVariant?.availableForSale);
      setQuantityAvailable(activeVariant?.quantityAvailable);
    }
  }, [activeVariant]);

  if (isNonCommerce) {
    return (
      <span>
        {activeVariant?.sku
          ? tt(s => s.productOptionsComponent.modelIDLabel, {
              sku: activeVariant.sku,
            })
          : ''}
      </span>
    );
  }

  const disableText = () => {
    if (activeVariant?.variantLifecycle !== null) {
      switch (activeVariant?.variantLifecycle) {
        case 'Obsolete':
          return tt(s => s.productOptionsComponent.discontinuedButton);
        case 'Coming Soon':
          return tt(s => s.productOptionsComponent.comingSoonButton);
        default:
          return tt(s => s.productOptionsComponent.outOfStockButton);
      }
    }
    switch (productLifecycle) {
      case 'Obsolete':
        return tt(s => s.productOptionsComponent.discontinuedButton);
      case 'Coming Soon':
        return tt(s => s.productOptionsComponent.comingSoonButton);
      default:
        return tt(s => s.productOptionsComponent.outOfStockButton);
    }
  };

  return (
    <div
      className="flex items-center gap-4 w-full flex-wrap"
      id="cart_and_quantity"
    >
      {!isObsoleteOrComingSoon(activeVariant?.variantLifecycle) &&
        !isObsoleteOrComingSoon(productLifecycle) &&
        activeVariant?.quantityAvailable > 0 && (
          <QuantityCounter
            disableManualEntry
            isDisabled={!isAvailableForSale}
            max={quantityAvailable > 0 ? quantityAvailable : 10}
            quantity={quantity}
            setQuantity={setQuantity}
          />
        )}
      {isObsoleteOrComingSoon(activeVariant?.variantLifecycle) ||
      isObsoleteOrComingSoon(productLifecycle) ? (
        <Button disabled variant="cta">
          {disableText()}
        </Button>
      ) : (
        <>
          {activeVariant?.availableForSale &&
            activeVariant?.quantityAvailable > 0 && (
              <LargeCartButton
                className={cn(buttonFull ? 'w-full' : '')}
                disabled={!isAvailableForSale}
                title={tt(s => s.productOptionsComponent.addToCartButton)}
                aria-label={`${tt(
                  s => s.productOptionsComponent.addToCartButton
                )} ${product.shopifyProduct.title}`}
                type="submit"
                variant="cta"
              />
            )}
          {!isAvailableForSale && (
            <Button disabled variant="cta">
              {tt(s => s.productOptionsComponent.outOfStockButton)}
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export default AddToCartWithQuantity;
